import React, { memo } from 'react';
import { Pencil, ArrowLeftCircle, Trash2, Send, CheckCircle, CheckSquare, Square } from 'lucide-react';
import type { Product } from '../lib/types';

interface ProductListProps {
  products: Product[];
  view: 'grid' | 'list';
  searchTerm: string;
  onEdit: (product: Product) => void;
  onStatusChange?: (id: number) => void;
  onDelete?: (id: number) => void;
  onWooCommerce?: (product: Product) => void;
  actionIcon?: 'restore' | 'complete';
  selectedProducts?: number[];
  onSelectProduct?: (id: number, selected: boolean) => void;
}

// Utilisation de React.memo pour éviter les re-renders inutiles
export const ProductList = memo(function ProductList({
  products,
  view,
  searchTerm,
  onEdit,
  onStatusChange,
  onDelete,
  onWooCommerce,
  actionIcon = 'complete',
  selectedProducts = [],
  onSelectProduct
}: ProductListProps) {
  const getActionIcon = () => {
    if (actionIcon === 'restore') {
      return <ArrowLeftCircle className="w-5 h-5" />;
    }
    return <CheckCircle className="w-5 h-5" />;
  };

  const getActionTitle = () => {
    if (actionIcon === 'restore') {
      return 'Retour au Live';
    }
    return 'Marquer comme en cours';
  };

  const filteredProducts = React.useMemo(() => products.filter(product => 
    product.client_number.toLowerCase().includes(searchTerm.toLowerCase())
  ), [products, searchTerm]);

  const parseItems = (itemsString: string): string[] => {
    // Return empty array if itemsString is empty, null, or undefined
    if (!itemsString || typeof itemsString !== 'string') {
      return [];
    }
    
    // Check if the string looks like JSON (starts with [ and ends with ])
    if (!itemsString.trim().startsWith('[') || !itemsString.trim().endsWith(']')) {
      // If it's not JSON array format, treat it as a single item
      return [itemsString];
    }
    
    try {
      const parsed = JSON.parse(itemsString);
      if (Array.isArray(parsed)) {
        return parsed;
      } else {
        // If parsed successfully but not an array, return as single item
        return [String(parsed)];
      }
    } catch (error) {
      console.error('Failed to parse items:', itemsString, error);
      // If parsing fails, return the string as a single item instead of an empty array
      return [itemsString];
    }
  };

  const isSelected = (id: number | undefined) => {
    if (!id || !selectedProducts) return false;
    return selectedProducts.includes(id);
  };

  const toggleSelection = (id: number | undefined) => {
    if (!id || !onSelectProduct) return;
    onSelectProduct(id, !isSelected(id));
  };

  // ProductCard component extracted for better performance
  const ProductCard = memo(({ product }: { product: Product }) => (
    <div key={product.id} className={`bg-white rounded-lg shadow p-6 ${isSelected(product.id) ? 'ring-2 ring-blue-500' : ''}`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center">
          {onSelectProduct && (
            <button
              onClick={() => toggleSelection(product.id)}
              className="mr-2 text-gray-600 hover:text-blue-600"
              title={isSelected(product.id) ? "Désélectionner" : "Sélectionner"}
            >
              {isSelected(product.id) ? 
                <CheckSquare className="w-5 h-5 text-blue-600" /> : 
                <Square className="w-5 h-5" />
              }
            </button>
          )}
          <div>
            <h3 className="text-lg font-semibold">Client #{product.client_number}</h3>
            <p className="text-sm text-gray-500">
              {new Date(product.created_at || '').toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(product)}
            className="text-blue-600 hover:text-blue-900"
            title="Modifier"
          >
            <Pencil className="w-5 h-5" />
          </button>
          {onStatusChange && product.id && (
            <button
              onClick={() => onStatusChange(product.id!)}
              className="text-green-600 hover:text-green-900"
              title={getActionTitle()}
            >
              {getActionIcon()}
            </button>
          )}
          {onWooCommerce && (
            <button
              onClick={() => onWooCommerce(product)}
              className="text-purple-600 hover:text-purple-900"
              title="Envoyer vers WooCommerce"
            >
              <Send className="w-5 h-5" />
            </button>
          )}
          {onDelete && product.id && (
            <button
              onClick={() => onDelete(product.id!)}
              className="text-red-600 hover:text-red-900"
              title="Supprimer"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      
      <div className="space-y-2">
        {parseItems(product.items).map((item: string, index: number) => (
          <div key={index} className="text-sm text-gray-600">
            {item}
          </div>
        ))}
      </div>
      
      <div className="mt-4 pt-4 border-t">
        <div className="flex justify-between items-center">
          <span className="font-semibold">Total:</span>
          <span className="text-lg font-bold">{product.total_price}€</span>
        </div>
      </div>
    </div>
  ));

  // ProductRow component extracted for better performance
  const ProductRow = memo(({ product }: { product: Product }) => (
    <tr 
      key={product.id} 
      className={isSelected(product.id) ? 'bg-blue-50' : ''}
    >
      {onSelectProduct && (
        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
          <button 
            onClick={() => toggleSelection(product.id)}
            className="text-gray-600 hover:text-blue-600"
          >
            {isSelected(product.id) ? 
              <CheckSquare className="w-5 h-5 text-blue-600" /> : 
              <Square className="w-5 h-5" />
            }
          </button>
        </td>
      )}
      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
        #{product.client_number}
      </td>
      <td className="px-3 py-4 text-sm text-gray-500">
        {parseItems(product.items).join(', ')}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {product.total_price}€
      </td>
      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => onEdit(product)}
            className="text-blue-600 hover:text-blue-900"
            title="Modifier"
          >
            <Pencil className="w-5 h-5" />
          </button>
          {onStatusChange && product.id && (
            <button
              onClick={() => onStatusChange(product.id!)}
              className="text-green-600 hover:text-green-900"
              title={getActionTitle()}
            >
              {getActionIcon()}
            </button>
          )}
          {onWooCommerce && (
            <button
              onClick={() => onWooCommerce(product)}
              className="text-purple-600 hover:text-purple-900"
              title="Envoyer vers WooCommerce"
            >
              <Send className="w-5 h-5" />
            </button>
          )}
          {onDelete && product.id && (
            <button
              onClick={() => onDelete(product.id!)}
              className="text-red-600 hover:text-red-900"
              title="Supprimer"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  ));

  if (view === 'grid') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
      <table className="min-w-[800px] w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            {onSelectProduct && (
              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-10">
                <span className="sr-only">Sélection</span>
              </th>
            )}
            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-32">
              Client
            </th>
            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              Articles
            </th>
            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-32">
              Total
            </th>
            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6 w-40">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {filteredProducts.map(product => (
            <ProductRow key={product.id} product={product} />
          ))}
        </tbody>
      </table>
    </div>
  );
});